/**
 * PlanViewer — Wiederverwendbare Plan-Anzeige mit optionalem Pin-Overlay.
 *
 * Modi:
 *   - mode="view"   → Plan anzeigen, optional vorhandene Marker rendern (klickbar)
 *   - mode="picker" → Klick auf Bild/PDF-Container setzt einen neuen Marker
 *
 * Inputs:
 *   plan: { id, file_type, has_preview, page_count, original_filename, ... }
 *   src: vollständige URL zur Datei oder zum Preview
 *   srcPreview: optionaler URL für Vorschau-PNG (von PDFs)
 *   markers: [{ id, x_percent, y_percent, title, status, task_number }]
 *   selectedMarkerId: ID des hervorgehobenen Markers
 *   onMarkerClick: (markerId) => void
 *   onPickLocation: ({x_percent, y_percent, page}) => void  (nur picker-mode)
 *   currentPin: { x_percent, y_percent } – aktuell gewählter Pin (picker-mode)
 *   compact: kompakte Höhe für Inline-Vorschauen
 */
import { useRef, useState, useCallback, useEffect } from "react";
import { MapPin, FileText, Download, AlertTriangle, ZoomIn, ZoomOut, Maximize2 } from "lucide-react";
import AuthImage from "./AuthImage";

const STATUS_COLORS = {
  offen: "#9CA3AF",
  in_arbeit: "#3B82F6",
  erledigt: "#10B981",
  blockiert: "#EF4444",
};

const MIN_SCALE = 1;
const MAX_SCALE = 8;
const DRAG_THRESHOLD = 5; // px — unter diesem Wert gilt's als Klick, nicht Drag

export default function PlanViewer({
  plan,
  src,
  srcPreview,
  markers = [],
  selectedMarkerId = null,
  onMarkerClick,
  onPickLocation,
  currentPin = null,
  compact = false,
  mode = "view",
  downloadHref = null,
  // For picker mode on PDFs: which page is shown (1-based). Defaults to 1.
  page = 1,
  // Wenn true, lädt Vorschau via authFetch (für interne Seiten).
  // Public Shared Page setzt das auf false und nutzt direkte URLs.
  authenticated = true,
}) {
  const containerRef = useRef(null);
  const [imgError, setImgError] = useState(false);

  // Zoom + Pan State
  const [transform, setTransform] = useState({ scale: 1, x: 0, y: 0 });
  const dragRef = useRef({ active: false, startX: 0, startY: 0, baseX: 0, baseY: 0, moved: false });
  const pinchRef = useRef({ active: false, startDist: 0, startScale: 1, centerX: 0, centerY: 0, baseX: 0, baseY: 0 });

  const resetView = useCallback(() => setTransform({ scale: 1, x: 0, y: 0 }), []);

  // Clamp pan so image bleibt sichtbar
  const clampPan = (s, x, y) => {
    const c = containerRef.current;
    if (!c) return { x, y };
    const w = c.clientWidth, h = c.clientHeight;
    const overW = w * (s - 1);
    const overH = h * (s - 1);
    return {
      x: Math.max(-overW, Math.min(0, x)),
      y: Math.max(-overH, Math.min(0, y)),
    };
  };

  // Zoom um Fokuspunkt (cx, cy relativ zum Container)
  const zoomAt = useCallback((newScale, cx, cy) => {
    setTransform((t) => {
      const s2 = Math.max(MIN_SCALE, Math.min(MAX_SCALE, newScale));
      if (s2 === t.scale) return t;
      // Punkt unter Maus soll an gleicher Stelle bleiben:
      // imgPt_x = (cx - t.x) / t.scale  ;  neu: (cx - x2) / s2 = imgPt_x → x2 = cx - imgPt_x * s2
      const imgX = (cx - t.x) / t.scale;
      const imgY = (cy - t.y) / t.scale;
      const x2 = cx - imgX * s2;
      const y2 = cy - imgY * s2;
      const clamped = clampPan(s2, x2, y2);
      return { scale: s2, x: clamped.x, y: clamped.y };
    });
  }, []);

  // Mausrad-Zoom
  const handleWheel = useCallback((e) => {
    if (!containerRef.current) return;
    e.preventDefault();
    const rect = containerRef.current.getBoundingClientRect();
    const cx = e.clientX - rect.left;
    const cy = e.clientY - rect.top;
    const factor = e.deltaY < 0 ? 1.15 : 1 / 1.15;
    setTransform((t) => {
      const s2 = Math.max(MIN_SCALE, Math.min(MAX_SCALE, t.scale * factor));
      if (s2 === t.scale) return t;
      const imgX = (cx - t.x) / t.scale;
      const imgY = (cy - t.y) / t.scale;
      const x2 = cx - imgX * s2;
      const y2 = cy - imgY * s2;
      const clamped = clampPan(s2, x2, y2);
      return { scale: s2, x: clamped.x, y: clamped.y };
    });
  }, []);

  // useEffect für non-passive wheel + cleanup
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    el.addEventListener("wheel", handleWheel, { passive: false });
    return () => el.removeEventListener("wheel", handleWheel);
  }, [handleWheel]);

  // Mouse drag = pan
  const handleMouseDown = (e) => {
    if (e.button !== 0) return;
    dragRef.current = { active: true, startX: e.clientX, startY: e.clientY, baseX: transform.x, baseY: transform.y, moved: false };
  };
  const handleMouseMove = (e) => {
    if (!dragRef.current.active) return;
    const dx = e.clientX - dragRef.current.startX;
    const dy = e.clientY - dragRef.current.startY;
    if (Math.abs(dx) + Math.abs(dy) > DRAG_THRESHOLD) dragRef.current.moved = true;
    if (transform.scale === 1) return; // Bei scale=1 kein Pan
    const clamped = clampPan(transform.scale, dragRef.current.baseX + dx, dragRef.current.baseY + dy);
    setTransform({ ...transform, x: clamped.x, y: clamped.y });
  };
  const handleMouseUp = () => { dragRef.current.active = false; };

  // Touch: Single = Pan, Double = Pinch
  const dist2 = (a, b) => Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);
  const center2 = (a, b) => ({ x: (a.clientX + b.clientX) / 2, y: (a.clientY + b.clientY) / 2 });
  const handleTouchStart = (e) => {
    if (e.touches.length === 2 && containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      const c = center2(e.touches[0], e.touches[1]);
      pinchRef.current = {
        active: true,
        startDist: dist2(e.touches[0], e.touches[1]),
        startScale: transform.scale,
        centerX: c.x - rect.left,
        centerY: c.y - rect.top,
        baseX: transform.x,
        baseY: transform.y,
      };
    } else if (e.touches.length === 1) {
      const t = e.touches[0];
      dragRef.current = { active: true, startX: t.clientX, startY: t.clientY, baseX: transform.x, baseY: transform.y, moved: false };
    }
  };
  const handleTouchMove = (e) => {
    if (pinchRef.current.active && e.touches.length === 2) {
      e.preventDefault();
      const d = dist2(e.touches[0], e.touches[1]);
      const factor = d / pinchRef.current.startDist;
      zoomAt(pinchRef.current.startScale * factor, pinchRef.current.centerX, pinchRef.current.centerY);
    } else if (dragRef.current.active && e.touches.length === 1) {
      const t = e.touches[0];
      const dx = t.clientX - dragRef.current.startX;
      const dy = t.clientY - dragRef.current.startY;
      if (Math.abs(dx) + Math.abs(dy) > DRAG_THRESHOLD) dragRef.current.moved = true;
      if (transform.scale > 1) {
        const clamped = clampPan(transform.scale, dragRef.current.baseX + dx, dragRef.current.baseY + dy);
        setTransform((tr) => ({ ...tr, x: clamped.x, y: clamped.y }));
      }
    }
  };
  const handleTouchEnd = (e) => {
    if (e.touches.length === 0) {
      dragRef.current.active = false;
      pinchRef.current.active = false;
    } else if (e.touches.length === 1) {
      pinchRef.current.active = false;
    }
  };

  if (!plan) return null;

  const ft = (plan.file_type || "").toLowerCase();
  const isImg = ["png", "jpg", "jpeg", "webp"].includes(ft);
  const isPdf = ft === "pdf";
  const isCad = ["dwg", "dxf"].includes(ft);

  // Picker-Mode: Wir nutzen IMMER das Preview-Bild (PDF→PNG) als Klickfläche,
  // damit Pin-Koordinaten zuverlässig auf einer statischen Fläche landen.
  const useImageSurface = mode === "picker" || (isImg && !imgError) || (isPdf && plan.has_preview);
  const surfaceSrc = mode === "picker" || isPdf ? (srcPreview || src) : src;

  const handleSurfaceClick = (e) => {
    if (mode !== "picker" || !onPickLocation || !containerRef.current) return;
    if (dragRef.current.moved) return; // Drag, kein Klick
    const rect = containerRef.current.getBoundingClientRect();
    const cx = e.clientX - rect.left;
    const cy = e.clientY - rect.top;
    // Berücksichtige Zoom + Pan
    const imgX = (cx - transform.x) / transform.scale;
    const imgY = (cy - transform.y) / transform.scale;
    const x_percent = (imgX / rect.width) * 100;
    const y_percent = (imgY / rect.height) * 100;
    onPickLocation({
      x_percent: Math.max(0, Math.min(100, x_percent)),
      y_percent: Math.max(0, Math.min(100, y_percent)),
      page,
    });
  };

  const heightCls = compact
    ? "min-h-[220px] max-h-[60vh]"
    : "min-h-[400px] h-[calc(100vh-260px)]";

  // CAD: kein Preview → Download-Hinweis
  if (isCad) {
    return (
      <div className={`bg-white/5 border border-white/10 rounded-lg flex flex-col items-center justify-center text-center p-8 ${heightCls}`}>
        <AlertTriangle className="w-10 h-10 text-amber-400 mb-3" />
        <p className="text-sm text-gray-200 font-medium mb-1">CAD-Datei: {ft.toUpperCase()}</p>
        <p className="text-xs text-gray-400 mb-4">Keine Vorschau möglich. Bitte herunterladen und in CAD-Software öffnen.</p>
        {downloadHref && (
          <a
            href={downloadHref}
            download={plan.original_filename}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-blue-600 hover:bg-blue-700 text-white text-sm"
            data-testid="plan-cad-download"
          >
            <Download className="w-4 h-4" />
            Herunterladen ({plan.original_filename})
          </a>
        )}
      </div>
    );
  }

  // Bild-Oberfläche (Bilder + PDF-Preview) — pin-fähig, mit Zoom + Pan
  if (useImageSurface && surfaceSrc) {
    const ImageComponent = authenticated ? AuthImage : "img";
    const transformStyle = {
      transform: `translate(${transform.x}px, ${transform.y}px) scale(${transform.scale})`,
      transformOrigin: "0 0",
      width: "100%",
      height: "100%",
      position: "relative",
    };
    const cursorClass = transform.scale > 1
      ? (dragRef.current.active ? "cursor-grabbing" : "cursor-grab")
      : (mode === "picker" ? "cursor-crosshair" : "cursor-default");
    const counterScale = 1 / transform.scale;
    return (
      <div
        ref={containerRef}
        className={`relative bg-white/5 border border-white/10 rounded-lg overflow-hidden ${heightCls} ${cursorClass} touch-none select-none`}
        onClick={handleSurfaceClick}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onDoubleClick={resetView}
        data-testid="plan-viewer-surface"
      >
        <div style={transformStyle}>
          <ImageComponent
            src={surfaceSrc}
            alt={plan.title || plan.original_filename}
            className="w-full h-full object-contain select-none pointer-events-none"
            draggable={false}
            onError={() => setImgError(true)}
          />
          {/* Marker-Pins skalieren mit aber Icon bleibt fix-Größe */}
          {markers.map((m) => {
            const x = m.x_percent ?? m?.task_location_marker?.x_percent;
            const y = m.y_percent ?? m?.task_location_marker?.y_percent;
            if (x == null || y == null) return null;
            const color = STATUS_COLORS[m.status] || "#3B82F6";
            const isSel = selectedMarkerId === m.id;
            return (
              <button
                key={m.id}
                type="button"
                onClick={(e) => { e.stopPropagation(); onMarkerClick && onMarkerClick(m.id); }}
                className={`absolute p-0.5 transition-transform ${isSel ? "z-20" : "z-10"}`}
                style={{
                  left: `${x}%`, top: `${y}%`,
                  transform: `translate(-50%, -100%) scale(${counterScale * (isSel ? 1.25 : 1)})`,
                  transformOrigin: "50% 100%",
                }}
                title={`${m.task_number || ""} ${m.title || ""}`.trim()}
                data-testid={`plan-marker-${m.id}`}
              >
                <MapPin
                  className="w-7 h-7 drop-shadow-lg"
                  style={{ color, fill: isSel ? color : "rgba(255,255,255,0.85)", strokeWidth: 2 }}
                />
              </button>
            );
          })}
          {currentPin && currentPin.x_percent != null && (
            <div
              className="absolute pointer-events-none z-30"
              style={{
                left: `${currentPin.x_percent}%`, top: `${currentPin.y_percent}%`,
                transform: `translate(-50%, -100%) scale(${counterScale})`,
                transformOrigin: "50% 100%",
              }}
              data-testid="plan-current-pin"
            >
              <MapPin className="w-9 h-9 drop-shadow-2xl animate-pulse" style={{ color: "#F59E0B", fill: "#F59E0B" }} />
            </div>
          )}
        </div>

        {/* Zoom-Controls (rechts oben) */}
        <div className="absolute top-2 right-2 flex flex-col gap-1 z-40">
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); const c = containerRef.current; if (!c) return; const r = c.getBoundingClientRect(); zoomAt(transform.scale * 1.4, r.width / 2, r.height / 2); }}
            className="w-8 h-8 rounded bg-black/60 hover:bg-black/80 text-white flex items-center justify-center backdrop-blur"
            title="Vergrößern"
            data-testid="plan-zoom-in"
          ><ZoomIn className="w-4 h-4" /></button>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); const c = containerRef.current; if (!c) return; const r = c.getBoundingClientRect(); zoomAt(transform.scale / 1.4, r.width / 2, r.height / 2); }}
            className="w-8 h-8 rounded bg-black/60 hover:bg-black/80 text-white flex items-center justify-center backdrop-blur"
            title="Verkleinern"
            data-testid="plan-zoom-out"
          ><ZoomOut className="w-4 h-4" /></button>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); resetView(); }}
            className="w-8 h-8 rounded bg-black/60 hover:bg-black/80 text-white flex items-center justify-center backdrop-blur"
            title="Ansicht zurücksetzen"
            data-testid="plan-zoom-reset"
          ><Maximize2 className="w-4 h-4" /></button>
        </div>

        {/* Zoom-Indikator */}
        {transform.scale > 1 && (
          <div className="absolute top-2 left-2 z-40 text-[11px] text-white bg-black/60 backdrop-blur px-2 py-1 rounded font-mono">
            {Math.round(transform.scale * 100)}%
          </div>
        )}

        {mode === "picker" && (
          <div className="absolute bottom-2 left-2 right-12 text-center text-[11px] text-white/80 bg-black/40 rounded px-2 py-1 pointer-events-none">
            Tippe auf den Plan, um den Standort zu markieren · Pinch / Mausrad zum Zoomen
          </div>
        )}
      </div>
    );
  }

  // PDF ohne Preview → Fallback iframe (kein Pin-Overlay verfügbar)
  if (isPdf && src) {
    return (
      <div className={`bg-white/5 border border-white/10 rounded-lg overflow-hidden ${heightCls}`}>
        <iframe
          src={src}
          title={plan.title || plan.original_filename}
          className="w-full h-full"
          data-testid="plan-viewer-iframe"
        />
      </div>
    );
  }

  return (
    <div className={`bg-white/5 border border-white/10 rounded-lg flex items-center justify-center ${heightCls}`}>
      <div className="text-center">
        <FileText className="w-10 h-10 text-gray-500 mx-auto mb-2" />
        <p className="text-sm text-gray-400">Vorschau nicht verfügbar</p>
      </div>
    </div>
  );
}
